// MaturaInscribedCentralBaseAngleClosedTask.js
// Zadanie maturalne zamkniete: kat wpisany, kat srodkowy i kat przy promieniu.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function uniqueAngles(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    if (value > 0 && value < 180 && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  });
  return out;
}

function makeOptions(correct, gamma, central) {
  let values = uniqueAngles([
    correct,
    gamma,
    central,
    180 - central,
    correct + 10,
    correct - 10,
    gamma - 10,
    gamma + 10,
  ]);

  let delta = 15;
  while (values.length < 4) {
    values = uniqueAngles([...values, correct + delta, correct - delta]);
    delta += 5;
  }

  return shuffle(values.slice(0, 4)).map((angle, idx) => ({
    label: LETTERS[idx],
    value: angle,
    latex: `${angle}^{\\circ}`,
    isCorrect: angle === correct,
  }));
}

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function labelOutside(point, center, offset = 16) {
  const vx = point.x - center.x;
  const vy = point.y - center.y;
  const len = Math.hypot(vx, vy) || 1;
  const ux = vx / len;
  const uy = vy / len;
  return {
    x: point.x + ux * offset,
    y: point.y + uy * offset,
    anchor: ux < -0.25 ? "end" : ux > 0.25 ? "start" : "middle",
  };
}

function unit(vx, vy) {
  const len = Math.hypot(vx, vy) || 1;
  return { x: vx / len, y: vy / len };
}

function CircleBaseAngleDiagram({ gamma }) {
  const size = 320;
  const cx = 160;
  const cy = 166;
  const r = 114;
  const central = 2 * gamma;
  const half = central / 2;
  const aAngle = 90 + half;
  const bAngle = 90 - half;
  const cAngle = 270;
  const A = polar(cx, cy, r, aAngle);
  const B = polar(cx, cy, r, bAngle);
  const C = polar(cx, cy, r, cAngle);
  const O = { x: cx, y: cy };
  const center = { x: cx, y: cy };
  const line = "currentColor";
  const given = "#f59e0b";
  const searched = "#8b5cf6";

  const angleArcData = (vertex, p1, p2, radius) => {
    const a1 = Math.atan2(p1.y - vertex.y, p1.x - vertex.x);
    const a2 = Math.atan2(p2.y - vertex.y, p2.x - vertex.x);
    const start = {
      x: vertex.x + radius * Math.cos(a1),
      y: vertex.y + radius * Math.sin(a1),
    };
    const end = {
      x: vertex.x + radius * Math.cos(a2),
      y: vertex.y + radius * Math.sin(a2),
    };
    let diff = a2 - a1;
    while (diff <= -Math.PI) diff += 2 * Math.PI;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    const middleAngle = a1 + diff / 2;
    const mid = {
      x: vertex.x + (radius + 18) * Math.cos(middleAngle),
      y: vertex.y + (radius + 18) * Math.sin(middleAngle),
    };
    return {
      path: `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`,
      mid,
    };
  };

  const CLabel = labelOutside(C, center);
  const givenArc = angleArcData(C, B, A, 34);
  const searchedArc = angleArcData(B, A, O, 30);
  const cToBase = unit((A.x + B.x) / 2 - C.x, (A.y + B.y) / 2 - C.y);
  const givenLabel = {
    x: C.x + cToBase.x * 58,
    y: C.y + cToBase.y * 58,
  };
  const bToA = unit(A.x - B.x, A.y - B.y);
  const bToO = unit(O.x - B.x, O.y - B.y);
  const bBisector = unit(bToA.x + bToO.x, bToA.y + bToO.y);
  const searchedLabel = {
    x: B.x + bBisector.x * 45,
    y: B.y + bBisector.y * 45,
  };
  const LatexLabel = ({ x, y, math, color = line, width = 44, height = 26, anchor = "center" }) => (
    <foreignObject x={x - width / 2} y={y - height / 2} width={width} height={height}>
      <div style={{ color, fontSize: 16, textAlign: anchor, lineHeight: 1.2 }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", width: "100%", maxWidth: size, height: "auto", margin: "0 auto" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={line} strokeWidth="2" />
      <line x1={C.x} y1={C.y} x2={A.x} y2={A.y} stroke={line} strokeWidth="2" />
      <line x1={C.x} y1={C.y} x2={B.x} y2={B.y} stroke={line} strokeWidth="2" />
      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={line} strokeWidth="2" />
      <line x1={O.x} y1={O.y} x2={A.x} y2={A.y} stroke={line} strokeWidth="1.8" />
      <line x1={O.x} y1={O.y} x2={B.x} y2={B.y} stroke={line} strokeWidth="1.8" />

      <path
        d={givenArc.path}
        fill="none"
        stroke={given}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d={searchedArc.path}
        fill="none"
        stroke={searched}
        strokeWidth="3"
        strokeLinecap="round"
      />

      <circle cx={A.x} cy={A.y} r="3.5" fill={line} />
      <circle cx={B.x} cy={B.y} r="3.5" fill={line} />
      <circle cx={C.x} cy={C.y} r="3.5" fill={line} />
      <circle cx={O.x} cy={O.y} r="3.2" fill={line} />

      <LatexLabel x={A.x - 18} y={A.y + 14} math={"A"} />
      <LatexLabel x={B.x + 18} y={B.y + 14} math={"B"} />
      <LatexLabel x={CLabel.x} y={CLabel.y} math={"C"} />
      <LatexLabel x={O.x} y={O.y - 12} math={"O"} />
      <LatexLabel x={givenLabel.x} y={givenLabel.y} math={`${gamma}^{\\circ}`} color={given} width={52} />
      <LatexLabel x={searchedLabel.x} y={searchedLabel.y} math={"?"} color={searched} width={28} />
    </svg>
  );
}

function generateMay2025Original() {
  const gamma = 50;
  const central = 100;
  const answer = 40;
  const values = [20, 35, 40, 50];
  const options = values.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: `${value}^{\\circ}`,
    isCorrect: idx === 2,
  }));

  return {
    type: "MaturaInscribedCentralBaseAngleClosedTask",
    data: { gamma, central, answer, options, correctIndex: 2 },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 19,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const gamma = pick([30, 35, 40, 45, 50, 55, 60, 65, 70]);
  const central = 2 * gamma;
  const answer = 90 - gamma;
  const options = makeOptions(answer, gamma, central);
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaInscribedCentralBaseAngleClosedTask",
    data: {
      gamma,
      central,
      answer,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { gamma, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Punkty <InlineMath math={"A"} />, <InlineMath math={"B"} /> oraz{" "}
        <InlineMath math={"C"} /> leżą na okręgu o środku w punkcie{" "}
        <InlineMath math={"O"} />. Miara kąta <InlineMath math={"BCA"} /> jest równa{" "}
        <InlineMath math={`${gamma}^{\\circ}`} />.
      </p>

      <CircleBaseAngleDiagram gamma={gamma} />

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Miara kąta ostrego <InlineMath math={"ABO"} /> jest równa
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((option) => (
          <div key={option.label} style={{ marginBottom: 8 }}>
            <InlineMath math={`${option.label}.\\; ${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = data.options.map((option, idx) => ({
    value: idx,
    label: option.label,
    textNode: option.label,
  }));

  const handleChange = (idx) => {
    const option = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: option.label,
      latex: option.latex,
      value: option.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { gamma, central, answer } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Kąt środkowy oparty na tym samym łuku jest dwa razy
        większy od kąta wpisanego:
      </p>
      <BlockMath math={`\\angle AOB = 2 \\cdot ${gamma}^{\\circ} = ${central}^{\\circ}`} />

      <p>
        <strong>Krok 2.</strong> Odcinki <InlineMath math={"OA"} /> oraz{" "}
        <InlineMath math={"OB"} /> są promieniami tego samego okręgu, więc trójkąt{" "}
        <InlineMath math={"AOB"} /> jest równoramienny.
      </p>

      <p>
        <strong>Krok 3.</strong> Kąty przy podstawie tego trójkąta są równe:
      </p>
      <BlockMath
        math={`\\angle ABO = \\frac{180^{\\circ} - ${central}^{\\circ}}{2} = ${answer}^{\\circ}`}
      />

      <p>
        Zatem miara kąta <InlineMath math={"ABO"} /> jest równa{" "}
        <InlineMath math={`${answer}^{\\circ}`} />.
      </p>
    </div>
  );
}
